import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, ExternalLink, Copy, Sparkles, TrendingUp, Cookie, DollarSign, CheckCircle2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OfficialLinksPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [niche, setNiche] = useState("all");

  useEffect(() => {
    document.title = "Official Affiliate Links — Social Flats";
    axios.get(`${API}/official-links`).then((r) => setRows(r.data.programs || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const confirmed = !!(user && user.email_verified && user.handle);

  const niches = ["all", ...Array.from(new Set(rows.map((r) => r.niche_name).filter(Boolean)))];
  const filtered = rows.filter((r) => {
    if (niche !== "all" && r.niche_name !== niche) return false;
    if (q && !`${r.name} ${r.description || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const linkFor = (slug) => `${window.location.origin}/go/${user.handle}/${slug}`;

  const copyLink = async (slug, name) => {
    try {
      await navigator.clipboard.writeText(linkFor(slug));
      toast.success(`${name} link copied — you earn on every click`);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <main data-testid="official-links-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="mb-10 max-w-3xl">
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-emerald-700 mb-3">Official Links · Vetted &amp; Live</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-4">Ready-to-share affiliate links</h1>
        <p className="text-base text-slate-600 leading-relaxed">
          Every program below is <b>officially onboarded</b> to Social Flats — commissions verified, cookies confirmed, and paid out through your ambassador account.
          {confirmed ? " Copy any link and start earning immediately." : " Tracked links unlock once you join free and confirm your email."}
        </p>
        {!confirmed && (
          <Link to={user ? "/dashboard" : "/signup"} data-testid="official-signup-cta"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-500 transition-colors">
            {user ? "Confirm your email to unlock links" : "Join free · confirm email · get your links"} <Sparkles className="w-4 h-4" />
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search programs…"
          data-testid="official-search"
          className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 flex-1 min-w-[200px] max-w-sm" />
        <div className="flex flex-wrap gap-2">
          {niches.map((n) => (
            <button key={n} onClick={() => setNiche(n)} data-testid={`niche-filter-${n}`}
              className={`text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${niche === n ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-50"}`}>
              {n === "all" ? "All" : n}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading official links…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-16 text-center">
          <p className="text-slate-500 text-sm">No official links published yet. Check back soon — new programs are onboarded every week.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <div key={p.slug} data-testid={`official-card-${p.slug}`} className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col hover:border-emerald-300 transition-colors duration-200">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <Link to={`/official-links/${p.slug}`} data-testid={`detail-link-${p.slug}`} className="font-semibold text-slate-900 hover:text-emerald-700 transition-colors">{p.name}</Link>
                  <p className="text-xs text-slate-400 uppercase tracking-wider mt-0.5">{p.niche_name}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <CheckCircle2 className="w-3 h-3" /> {p.status === "live" ? "Live" : "Approved"}
                </span>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed mb-4 line-clamp-3 flex-1">{p.description || "—"}</p>
              <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
                <div className="bg-slate-50 rounded p-2 text-center">
                  <DollarSign className="w-3.5 h-3.5 mx-auto text-emerald-600" />
                  <p className="font-semibold text-slate-900 mt-1">{p.commission || "—"}</p>
                  <p className="text-slate-400">Comm.</p>
                </div>
                <div className="bg-slate-50 rounded p-2 text-center">
                  <Cookie className="w-3.5 h-3.5 mx-auto text-amber-600" />
                  <p className="font-semibold text-slate-900 mt-1">{p.cookie_days || "—"}d</p>
                  <p className="text-slate-400">Cookie</p>
                </div>
                <div className="bg-slate-50 rounded p-2 text-center">
                  <TrendingUp className="w-3.5 h-3.5 mx-auto text-indigo-600" />
                  <p className="font-semibold text-slate-900 mt-1">${(p.epc || 0).toFixed(2)}</p>
                  <p className="text-slate-400">EPC</p>
                </div>
              </div>
              {confirmed ? (
                <div className="flex gap-2">
                  <button onClick={() => copyLink(p.slug, p.name)} data-testid={`copy-${p.slug}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 transition-colors">
                    <Copy className="w-3.5 h-3.5" /> Copy my link
                  </button>
                  <a href={linkFor(p.slug)} target="_blank" rel="noopener noreferrer" data-testid={`open-${p.slug}`}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-slate-200 transition-colors">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              ) : (
                <Link to={user ? "/dashboard" : "/signup"} data-testid={`locked-${p.slug}`}
                  className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-500 transition-colors">
                  {user ? <ShieldCheck className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  {user ? "Confirm email to unlock" : "Join now to unlock"}
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
